// @ts-nocheck
/**
 * Utility functions for evaluating conditional rules to determine field visibility
 */

import { isValueEmpty } from './Utils';

/**
 * Extracts a comparable primitive from a conditional field or condition value.
 * Supports primitives and option objects such as { label, value }, preferring
 * value over label when both are present.
 *
 * @param {*} val - Field value or condition value to normalize.
 * @returns {*} Primitive value used for comparison.
 */
const getPrimitiveValue = (val) => {
  if (val && typeof val === 'object' && !Array.isArray(val)) {
    if (!isValueEmpty(val?.value)) {
      return val.value;
    }
    if (!isValueEmpty(val?.label)) {
      return val.label;
    }
  }
  return val;
};

/**
 * Normalizes conditional field values into an array for comparison.
 * Supports arrays, nested arrays, comma-separated strings, primitives,
 * and option objects such as { label, value }.
 *
 * @param {*} val - Field value or condition value to normalize.
 * @returns {Array} Normalized list of comparable values.
 */
const getArrayFromValue = (val) => {
  if (isValueEmpty(val)) {
    return [];
  }

  if (Array.isArray(val)) {
    return val.flatMap(getArrayFromValue);
  }

  const primitiveValue = getPrimitiveValue(val);

  if (isValueEmpty(primitiveValue)) {
    return [];
  }

  if (typeof primitiveValue === 'string') {
    return primitiveValue
      .split(',')
      .map((v) => v.trim())
      .filter((v) => !isValueEmpty(v));
  }

  return [primitiveValue];
};

/**
 * Converts a conditional field or condition value into a Set of strings.
 * Supports the same input shapes as getArrayFromValue and is used by
 * contains checks to test whether all configured values are selected.
 *
 * @param {*} val - Field value or condition value to convert.
 * @returns {Set<string>} Set of normalized comparable string values.
 */
const getStringValueSet = (val) => {
  return new Set(getArrayFromValue(val).map((item) => String(item)));
};

/**
 * Evaluates a single rule against form values
 * @param {Object} rule - The rule object
 * @param {string} rule.field - Field name to check
 * @param {string} rule.operator - Comparison operator (=, !=, >, <, etc.)
 * @param {any} rule.value - Value to compare against
 * @param {Object} formValues - Current form values
 * @returns {boolean} - Whether the rule evaluates to true
 */
export const evaluateRule = (rule, formValues) => {
  const { attribute_id, operator, value } = rule;

  // Priority order for field value lookup:
  // 1. Use attribute_id if available (most reliable)
  // 2. Use field name directly
  // 3. Try to map field name to attribute ID using attributes array
  let fieldValue = '';

  if (attribute_id) {
    // Use attribute_id if available (preferred method)
    fieldValue = formValues?.[attribute_id];
  }

  // If the field value is an array (like multi-select), extract the primitive values.
  if (Array.isArray(fieldValue)) {
    fieldValue = fieldValue?.map((item) => {
      if (item && typeof item === 'object' && item?.value !== undefined) {
        return item?.value;
      }
      return item;
    });
  }

  // Normalize null, undefined, and empty string to be treated as equivalent
  const normalizeValue = (val) => {
    if (isValueEmpty(val)) {
      return '';
    }
    return val;
  };

  const normalizedFieldValue = normalizeValue(fieldValue);
  const normalizedValue = normalizeValue(value);

  switch (operator) {
    case '=':
    case '==':
      if (Array.isArray(normalizedFieldValue)) {
        // Convert normalizedValue to array if it's a string
        const valArray = getArrayFromValue(normalizedValue);

        // Check for exact match (length and elements)
        if (normalizedFieldValue.length !== valArray.length) return false;

        // Sort both arrays to ensure order doesn't affect equality
        const sortedField = [...normalizedFieldValue].sort((a, b) =>
          String(a).localeCompare(String(b))
        );
        const sortedVal = [...valArray].sort((a, b) =>
          String(a).localeCompare(String(b))
        );

        return sortedField.every((v, i) => v === sortedVal[i]);
      }
      return normalizedFieldValue == normalizedValue;
    case '!=':
      if (Array.isArray(normalizedFieldValue)) {
        // Convert normalizedValue to array if it's a string
        const valArray = getArrayFromValue(normalizedValue);

        // Check for exact match (length and elements)
        if (normalizedFieldValue.length !== valArray.length) return true;

        // Sort both arrays to ensure order doesn't affect equality
        const sortedField = [...normalizedFieldValue].sort((a, b) =>
          String(a).localeCompare(String(b))
        );
        const sortedVal = [...valArray].sort((a, b) =>
          String(a).localeCompare(String(b))
        );

        return !sortedField.every((v, i) => v === sortedVal[i]);
      }
      return normalizedFieldValue != normalizedValue;
    case '>':
      return Number(fieldValue) > Number(value);
    case '>=':
      return Number(fieldValue) >= Number(value);
    case '<':
      return Number(fieldValue) < Number(value);
    case '<=':
      return Number(fieldValue) <= Number(value);
    case 'contains': {
      // Text / free-form values
      if (typeof fieldValue === 'string' && typeof value === 'string') {
        return fieldValue?.toLowerCase().includes(value?.toLowerCase());
      }
      const fieldValueSet = getStringValueSet(fieldValue);
      const conditionValues = getArrayFromValue(value);

      return (
        conditionValues.length > 0 &&
        conditionValues.every((conditionValue) =>
          fieldValueSet.has(String(conditionValue))
        )
      );
    }
    case 'startsWith':
      return String(fieldValue)
        .toLowerCase()
        .startsWith(String(value).toLowerCase());
    case 'endsWith':
      return String(fieldValue)
        .toLowerCase()
        .endsWith(String(value).toLowerCase());
    case 'isEmpty':
      return (
        !fieldValue ||
        fieldValue === '' ||
        (Array.isArray(fieldValue) && fieldValue.length === 0)
      );
    case 'isNotEmpty':
      return (
        fieldValue &&
        fieldValue !== '' &&
        (!Array.isArray(fieldValue) || fieldValue.length > 0)
      );
    default:
      console.warn(`Unknown operator: ${operator}`);
      return false;
  }
};

/**
 * Evaluates a group of rules with a combinator (AND/OR)
 * @param {Object} ruleGroup - The rule group object
 * @param {string} ruleGroup.combinator - 'and' or 'or'
 * @param {Array} ruleGroup.rules - Array of rules to evaluate
 * @param {Object} formValues - Current form values
 * @param {Array} [attributes] - Array of attribute objects for field mapping
 * @returns {boolean} - Whether the rule group evaluates to true
 */
export const evaluateRuleGroup = (ruleGroup, formValues) => {
  const { combinator, rules } = ruleGroup;

  if (!rules || rules.length === 0) {
    return true; // Empty rule group is considered true
  }

  const ruleResults = rules.map((rule) => {
    if (rule.rules) {
      // Nested rule group
      return evaluateRuleGroup(rule, formValues);
    } else {
      // Individual rule
      return evaluateRule(rule, formValues);
    }
  });

  if (combinator === 'or') {
    return ruleResults.some((result) => result);
  } else {
    // Default to 'and'
    return ruleResults.every((result) => result);
  }
};

/**
 * Determines if an attribute should be visible based on its conditional rule
 * @param {Object} attribute - The attribute object
 * @param {boolean} attribute.show_conditionally - Whether conditional logic should be applied
 * @param {Object} attribute.conditional_rule - The conditional rule to evaluate
 * @param {Object} formValues - Current form values
 * @param {Array} [allAttributes] - Array of all attribute objects for field mapping
 * @returns {boolean} - Whether the attribute should be visible
 */
export const shouldShowAttribute = (attribute, formValues) => {
  // If show_conditionally is not enabled, always show the attribute
  if (!attribute?.show_conditionally) {
    return true;
  }

  // If show_conditionally is true but no conditional_rule exists, show the attribute
  if (!attribute?.conditional_formula) {
    console.warn(
      'Attribute has show_conditionally=true but no conditional_rule'
    );
    return true;
  }

  try {
    // Evaluate the conditional rule
    const conditionResult = evaluateRuleGroup(
      attribute?.conditional_formula,
      formValues
    );

    // Show the attribute when condition is true, hide when false
    // (You mentioned hide when true, so you can flip this logic if needed)
    return conditionResult;
  } catch (error) {
    console.error('Error evaluating conditional rule for attribute:', error);
    return true; // Show attribute if evaluation fails
  }
};

/**
 * Filters attributes based on their conditional visibility rules
 * @param {Array} attributes - Array of attribute objects
 * @param {Object} formValues - Current form values
 * @returns {Array} - Filtered array of visible attributes
 */
export const getVisibleAttributes = (attributes, formValues) => {
  const visibleAttributes = [];
  const hiddenAttributes = [];
  attributes?.forEach((attribute) => {
    const value = shouldShowAttribute(attribute, formValues);
    if (value) {
      visibleAttributes.push(attribute);
    } else {
      hiddenAttributes.push(attribute);
    }
  });

  return { visibleAttributes, hiddenAttributes };
};
